import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../balance/balance-report-filters';
import { ReportFiltersVm } from '../data/report-filters.vm';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';
import '../edi/edi-report-filters';
import '../transaction/transaction-report-filters';

/**
 * Mobile/tablet: show filter bottom sheet like other view
 * Desktop: show filter panel across the top of the table
 */

const tagName = 'report-filters';
@customElement(tagName)
export class ReportFilters extends TmBaseComponent {
    @property({ type: Boolean })
    public filtersVisible = screen.width >= 1024;

    @property({ type: Boolean, reflect: true, attribute: 'as-desktop' })
    public asDesktop = false;

    @property({ type: Boolean })
    public hideActions = false;

    @property()
    public loading = false;

    @property()
    public report?: ReportMetaDataVm;

    @state()
    private filters: ReportFiltersVm = new ReportFiltersVm();

    private get range() {
        return this.report?.name.toLowerCase().includes('range');
    }

    public render() {
        if (!this.report) {
            return nothing;
        }
        if (this.loading) {
            return html`<div class="flex items-center justify-center h-20">
                <tm-loader card></tm-loader>
            </div>`;
        }
        // mobile filters
        if (screen.width < 1024 && !this.asDesktop) {
            return html`<tm-bottom-sheet
                .open=${this.filtersVisible}
                @close=${() => {
                    this.filtersVisible = false;
                    this.dispatchEvent(new CustomEvent('filters-closed'));
                }}
                ><span slot="header-center">Filter Report</span><span slot="header-right"></span>
                <tm-body .fullHeight=${false}>${this.filterFields}</tm-body>${this.renderFooter()}
            </tm-bottom-sheet>`;
        }
        // desktop filters
        return html`<div
            id="filter-well"
            class=${classMap({
                'filters border-b border-r border-l': true,
                'rounded-t': this.asDesktop,
                'filters-open': this.filtersVisible,
                'filters-hidden': !this.filtersVisible,
                '-translate-y-full': !this.filtersVisible,
                '-translate-y-100': this.filtersVisible,
                'overflow-scroll': this.filtersVisible,
            })}
        >
            <tm-body .shadow=${false}> ${this.filterFields}</tm-body> ${this.renderFooter()}
        </div>`;
    }

    // supply the correct filter fields based on the report type
    private get filterFields() {
        switch (this.report?.reportType.toLowerCase()) {
            case 'balance':
                return html`<balance-report-filters
                    .range=${this.range}
                    .filters=${this.filters}
                ></balance-report-filters>`;
            case 'edi':
                return html`<edi-report-filters .filters=${this.filters}></edi-report-filters>`;
            case 'transaction':
                return html`<transaction-report-filters
                    .range=${this.range}
                    .filters=${this.filters}
                ></transaction-report-filters>`;
            default:
                return nothing;
        }
    }

    private renderFooter() {
        if (this.hideActions) {
            return nothing;
        }
        return html`<tm-footer
            direction="row"
            .buttonConfig=${[
                {
                    text: 'Run',
                    importance: 'primary',
                    onClick: () => {
                        this.filtersVisible = screen.width >= 1024;
                        this.dispatchEvent(
                            new CustomEvent('filter', {
                                detail: { filters: this.filters },
                                bubbles: true,
                                composed: true,
                            })
                        );
                    },
                },
                {
                    text: 'Reset',
                    importance: 'secondary',
                    onClick: () => {
                        this.dispatchEvent(new CustomEvent('filters-reset'));
                        this.filters.reset();
                        this.loading = true;
                        setTimeout(() => {
                            this.loading = false;
                        }, 100);
                    },
                },
            ] as ButtonConfig[]}
        ></tm-footer>`;
    }

    public static get styles() {
        return css`
            :host {
                display: block;
            }
            #filter-well {
                transition: all 0.25s cubic-bezier(0, 0.76, 0.35, 1.04);
            }
            .filters-hidden {
                overflow: hidden;
                max-height: 0;
                transition: all 0.25s cubic-bezier(0.47, 0.41, 0, 1.78);
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ReportFilters;
    }
}
